import { ModelEntityType } from '@upright/common/model/input/types';
import { Valence } from '@upright/common/model/types';
import { middlePartWidth } from '@upright/common/renderer/renderer-settings';
import { get } from 'lodash';
import { FinalImpactScoreType, ImpactScoresByIVV } from '../../../../../../../common/model/output/scores/types';
import { BouColor } from '../../../../../generic/global/bou-colors';
import { ImpactChartDataSeries, ScaleMode } from './types';

export type ImpactChartHeights = ReturnType<typeof getHeights>;

export function getHeights(_seriesCount: number) {
  const seriesCount = _seriesCount || 1;

  const dimensionRowBarHeight = 14;
  const dimensionRowBarHeightHovered = 16;

  const dimensionRowSeriesSpacing = 4;
  const dimensionRowMargin = 16;
  const dimensionRow =
    seriesCount * dimensionRowBarHeight + (seriesCount - 1) * dimensionRowSeriesSpacing + 2 * dimensionRowMargin;
  const dimensionRowExpanded = seriesCount > 1 ? dimensionRow : dimensionRow * 0.7;
  const dimensionRowLeftPart = dimensionRow;

  // padding for the block containing all the impacts
  const impactBlockPaddingTop = 10;
  const impactBlockPaddingBottom = 18;

  // height for the bar for a single impact
  const impactRowBarHeight = 6;
  const impactRowBarHeightHovered = 8;

  // spacing between data series for a single impact
  const impactRowSeriesSpacing = 6;

  // spacing between two impacts
  const impactsSpacing = 14;

  // height of an impact row
  const impactRow = seriesCount * impactRowBarHeight + (seriesCount - 1) * impactRowSeriesSpacing;

  const bottomBoxPadding = 18;

  return {
    columnTitlesRow: 18,

    dimensionRow,
    dimensionRowExpanded,
    dimensionRowLeftPart,
    dimensionRowSeriesSpacing,
    dimensionRowMargin,
    dimensionRowBarHeight,
    dimensionRowBarHeightHovered,

    impactBlockPaddingTop,
    impactBlockPaddingBottom,

    impactRow,
    impactRowBarHeight,
    impactRowBarHeightHovered,

    impactRowSeriesSpacing,
    impactsSpacing,

    netScoreRow: 48,
    bottomBoxPadding,
  };
}

export function getWidths(fullWidth: number, desktopMode: boolean, staticMode: boolean, simplify: boolean) {
  const commonHorizontalPadding = 22;
  const middle = simplify ? commonHorizontalPadding : middlePartWidth;
  const title = desktopMode ? 200 : fullWidth - middle;
  const bars = desktopMode ? (fullWidth - title - middle) / 2 : 0;
  const titleTextPadding = 52 - 8;
  const barRange = bars - 60;

  const titleIconLeftLine = staticMode ? commonHorizontalPadding : titleTextPadding;
  const titleTextLeftLine = titleIconLeftLine + 25;

  return {
    full: fullWidth,
    title,
    middle,
    bars,
    barRange,
    titleTextPadding,
    titleIconLeftLine,
    titleTextLeftLine,
    commonHorizontalPadding,
    desktopMode,
  };
}

export const grayBorder = BouColor.IMPACT_BORDER_GRAY;

export function plusIfPositive(value: number) {
  if (value > 0) {
    return '+';
  }
  return '';
}

export function signForValenceAndValue(valence: Valence, value: number) {
  switch (valence) {
    case Valence.NEGATIVE:
      if (value > 0) return '-';
      else return '';
    case Valence.POSITIVE:
    case Valence.OTHER:
      if (value > 0) return '+';
      else return '';
  }
}

interface AbsoluteRelativeDataProps {
  relativeMode: boolean;
  data: ImpactChartDataSeries[];
}

export function getNumberData(props: AbsoluteRelativeDataProps, index: number): ImpactScoresByIVV | undefined {
  const data = get(props.data, index, undefined);
  if (!data?.impactScoresByTIVV) {
    return undefined;
  }
  return props.relativeMode
    ? data.impactScoresByTIVV[FinalImpactScoreType.RELATIVE]
    : data.impactScoresByTIVV[FinalImpactScoreType.ABSOLUTE];
}

export function getScaleMode(relativeMode: boolean, dataLength: number) {
  if (relativeMode || dataLength === 1) {
    return ScaleMode.DEFAULT;
  }
  return ScaleMode.ABSOLUTE;
}

export function allDataHasAbsolute(data: ImpactChartDataSeries[] | undefined) {
  if (!data) {
    return false;
  }
  const missing = data.find(d => !d.impactScoresByTIVV || !d.impactScoresByTIVV[FinalImpactScoreType.ABSOLUTE]);
  return !missing;
}

export function dataHasGroupfolios(data: Array<{ type: ModelEntityType }> | undefined) {
  if (!data) {
    return false;
  }
  return data.some(d => d.type === ModelEntityType.GROUP || d.type === ModelEntityType.PORTFOLIO);
}
